import React, { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { colors } from "../../styles/theme";
import { BsClipboardDataFill, BsGlobe } from "react-icons/bs";
import { FaMobile } from "react-icons/fa";
import { HiServer } from "react-icons/hi2";
import { MdPhoneInTalk } from "react-icons/md";
import { AiOutlineRobot } from "react-icons/ai";

const ServicesSection = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [percentageVisible, setPercentageVisible] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => {
      const mediaQuery = window.matchMedia("(max-width: 768px)");
      setIsMobile(mediaQuery.matches);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (targetRef.current) {
        const targetRect = targetRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        const visibleHeight =
          Math.min(targetRect.bottom, windowHeight) -
          Math.max(targetRect.top, 0);
        const percent = (visibleHeight / targetRect.height) * 100;

        setPercentageVisible(percent);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const getRadius = (percentage: number) => {
    return 100 - (percentage / 100) * (isMobile ? 250 : 90) + "%";
  };

  return (
    <ServicesSectionContainer
      ref={targetRef}
      style={{
        borderRadius: `0 ${getRadius(percentageVisible)} 0 0`,
      }}
      id="services"
    >
      <Title>Our Services</Title>
      <Subtitle>Our Palette of Expertise</Subtitle>
      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard key={index}>
            <HolographicContainer>
              <HolographicCard>
                <span
                  style={{
                    display: "flex",
                    height: 40,
                    width: 40,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "black",
                    borderRadius: "50%",
                  }}
                >
                  {service?.icon}
                </span>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </HolographicCard>
            </HolographicContainer>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </ServicesSectionContainer>
  );
};

const services = [
  {
    icon: <BsGlobe size={20} color="white" />,
    title: "Web App Development",
    description:
      "We build responsive and robust web applications tailored to your business needs, ensuring seamless user experiences across all devices.",
  },
  {
    icon: <FaMobile size={20} color="white"/>,
    title: "Mobile App Development",
    description:
      "Our team specializes in creating intuitive and engaging mobile applications for both iOS and Android platforms, enhancing your mobile presence.",
  },
  {
    icon: <HiServer size={20} color="white"/>,
    title: "Server-side App Development",
    description:
      "We offer comprehensive server-side application development services, ensuring your backend systems are secure, scalable, and efficient.",
  },
  {
    icon: <BsClipboardDataFill size={20} color="white"/>,
    title: "Data Science",
    description:
      "Leverage the power of data with our data science services, providing insights and analytics to drive informed decision-making and business growth.",
  },
  {
    icon: <AiOutlineRobot size={20} color="white"/>,
    title: "Artificial Intelligence",
    description:
      "Integrate cutting-edge AI solutions into your business processes, enhancing automation, efficiency, and innovation.",
  },
  {
    icon: <MdPhoneInTalk size={20} color="white"/>,
    title: "Technology Consultation",
    description:
      "Our expert consultants provide strategic technology advice to help you navigate the digital landscape and achieve your business objectives.",
  },
];

const ServicesSectionContainer = styled.div`
  min-height: 50vh;
  padding: 2% 5%;
  background-color: #151515;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 100vh;
    height: max-content;
  }
`;

const Title = styled.h2`
  color: white;
  margin-bottom: 10px;
  font-size: 24px;
`;

const Subtitle = styled.h3`
  color: #888888;
  margin-bottom: 40px;
  font-size: 18px;
`;

const ServicesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  padding: 0 8%;

  @media (max-width: 768px) {
    padding: 0 3%;
    flex-direction: column;
  }
`;

const ServiceCard = styled.div`
  width: 32%;
  min-height: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const HolographicContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: #000;
`;

const HolographicCard = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  transition: all 0.5s ease;
  padding: 20px;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }

  &:hover::before {
    opacity: 1;
    transform: rotate(-45deg) translateY(100%);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      0deg, 
      transparent, 
      transparent 30%, 
      rgba(0, 0, 0, 0.3)
    );
    transform: rotate(-45deg);
    transition: all 0.5s ease;
    opacity: 0;
  }
`;

const ServiceTitle = styled.h4`
  color: black;
  margin-bottom: 5px;
  font-size: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const ServiceDescription = styled.p`
  color: black;
  text-align: center;
  font-size: 14px;
  line-height: 25px;
  position: relative;
  z-index: 2;
`;

export default ServicesSection;
